import type { OptionsDocument, QuoteDocument } from 'paintscout'
import { getObjectLabels } from '../../../util/get-object-labels'

export interface GetCartLinkButtonLabelArgs {
  options?: OptionsDocument
  quote?: QuoteDocument
  label?: string
}

/**
 * The Quick Cart trigger button (v2 presentation Quote block) persists its label as a
 * user-editable string, but its default was always seeded without invoice context, so it
 * permanently reads e.g. "View Estimate" even when later shown on an invoice. This recomputes
 * the label live: an empty label gets the current invoice-aware default, a label that still
 * matches the stale opposite-invoice-state default gets corrected, and anything else (a real
 * customization) is left untouched.
 */
export function getCartLinkButtonLabel({ options, quote, label }: GetCartLinkButtonLabelArgs): string {
  const isInvoice = !!quote?.is_invoice
  const currentLabel = `View ${getObjectLabels({ options, quote, invoice: isInvoice }).quote.value}`

  if (!label) {
    return currentLabel
  }

  // Detects "still the untouched default" by string match against the opposite invoice state,
  // since there's no separate flag recording whether a user typed this label themselves. If
  // getObjectLabels's default wording ever changes (e.g. "View Estimate" -> "View your Estimate"),
  // labels already saved under the old wording will stop matching and stay stale.
  const staleLabel = `View ${getObjectLabels({ options, quote, invoice: !isInvoice }).quote.value}`
  return label === staleLabel ? currentLabel : label
}
